import { z } from 'zod';

export const calendarSourceKindSchema = z.enum([
  'SELECTED_MEMBER',
]);
export type CalendarSourceKind = z.infer<typeof calendarSourceKindSchema>;

export const selectedMemberProviderSelectorSchema = z
  .object({
    provider: z.literal('GOOGLE'),
    memberExternalAccountId: z.number().int().positive(),
    providerCalendarId: z.string().trim().min(1),
  })
  .strict();
export type SelectedMemberProviderSelector = z.infer<typeof selectedMemberProviderSelectorSchema>;

const calendarQuerySourceBaseSchema = z
  .object({
    kind: calendarSourceKindSchema,
    memberId: z.number().int().positive(),
    isPrimary: z.boolean(),
    providerSelector: selectedMemberProviderSelectorSchema.optional(),
  })
  .strict();

export const calendarQuerySourceSchema = calendarQuerySourceBaseSchema;
export type CalendarQuerySource = z.infer<typeof calendarQuerySourceSchema>;

export const calendarSourceSchema = calendarQuerySourceBaseSchema
  .extend({
    id: z.string().min(1),
    label: z.string().min(1),
    sublabel: z.string().min(1).optional(),
  })
  .strict();
export type CalendarSource = z.infer<typeof calendarSourceSchema>;

export const calendarCursorSchema = z
  .object({
    start: z.string().datetime(),
    eventId: z.number().int().positive(),
  })
  .strict();
export type CalendarCursor = z.infer<typeof calendarCursorSchema>;

const MAX_CALENDAR_RANGE_DAYS = 93;
const MAX_CALENDAR_RANGE_MS = MAX_CALENDAR_RANGE_DAYS * 24 * 60 * 60 * 1000;

export const calendarQueryInputSchema = z
  .object({
    rangeStart: z.string().datetime(),
    rangeEnd: z.string().datetime(),
    showHubClashes: z.boolean(),
    pageSize: z.number().int().min(1).max(200),
    cursor: calendarCursorSchema.optional(),
    sources: z.array(calendarQuerySourceSchema).min(1).max(64),
  })
  .strict()
  .superRefine((value, ctx) => {
    const selectedSources = value.sources.filter((source) => source.kind === 'SELECTED_MEMBER');
    if (selectedSources.length !== value.sources.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sources'],
        message: 'All calendar sources must be SELECTED_MEMBER',
      });
    }
    if (selectedSources.some((source) => !source.isPrimary)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sources'],
        message: 'SELECTED_MEMBER sources must be marked primary',
      });
    }

    const start = new Date(value.rangeStart);
    const end = new Date(value.rangeEnd);
    if (end.getTime() <= start.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rangeEnd'],
        message: 'rangeEnd must be after rangeStart',
      });
    }

    if (end.getTime() - start.getTime() > MAX_CALENDAR_RANGE_MS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rangeEnd'],
        message: `Calendar range cannot exceed ${MAX_CALENDAR_RANGE_DAYS} days`,
      });
    }
  });
export type CalendarQueryInput = z.infer<typeof calendarQueryInputSchema>;

export const calendarEventStatusSchema = z.enum([
  'CONFIRMED',
  'TENTATIVE',
  'CANCELLED',
]);
export type CalendarEventStatus = z.infer<typeof calendarEventStatusSchema>;

export const calendarEventProviderSchema = z.enum([
  'GOOGLE_CALENDAR',
  'VIVPORT',
  'MICROSOFT_CALENDAR',
]);
export type CalendarEventProvider = z.infer<typeof calendarEventProviderSchema>;

export const fullDetailCalendarProjectionSchema = z
  .object({
    projection: z.literal('FULL_DETAIL'),
    eventId: z.number().int().positive(),
    start: z.string().datetime(),
    end: z.string().datetime(),
    isAllDay: z.boolean(),
    title: z.string(),
    status: calendarEventStatusSchema.nullable(),
    provider: calendarEventProviderSchema.nullable(),
    externallyAuthored: z.boolean(),
  })
  .strict();
export type FullDetailCalendarProjection = z.infer<typeof fullDetailCalendarProjectionSchema>;

export const busyOnlyCalendarProjectionSchema = z
  .object({
    projection: z.literal('BUSY_ONLY'),
    start: z.string().datetime(),
    end: z.string().datetime(),
    externallyAuthored: z.boolean(),
  })
  .strict();
export type BusyOnlyCalendarProjection = z.infer<typeof busyOnlyCalendarProjectionSchema>;

export const calendarProjectionSchema = z.discriminatedUnion('projection', [
  fullDetailCalendarProjectionSchema,
  busyOnlyCalendarProjectionSchema,
]);
export type CalendarProjection = z.infer<typeof calendarProjectionSchema>;

export const calendarQueryResultSchema = z
  .object({
    items: z.array(calendarProjectionSchema),
    nextCursor: calendarCursorSchema.nullable(),
  })
  .strict();
export type CalendarQueryResult = z.infer<typeof calendarQueryResultSchema>;
